
// @ts-nocheck
(() => {
  const DEFAULT_DEBOUNCE_MS = 200;

  function debounce(fn, waitMs) {
    let timer;
    return function debounced(...args) {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn.apply(this, args), waitMs);
    };
  }

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.resultsContainer = this.querySelector('[data-predictive-search]');
      this.status = this.querySelector('.predictive-search-status');
      this.form = this.querySelector('form');

      if (!this.input || !this.resultsContainer) return;

      this._initialResultsMarkup = this.resultsContainer.innerHTML;
      this._cache = new Map();
      this._abortController = null;

      this._onInput = debounce(() => this._handleInput(), DEFAULT_DEBOUNCE_MS);
      this.input.addEventListener('input', this._onInput);

      if (this.form) {
        this.form.addEventListener('reset', () => {
          window.setTimeout(() => this._clearResults(), 0);
        });
      }

      document.addEventListener('click', (event) => {
        const target = event && event.target;
        if (target instanceof Node && !this.contains(target)) this._clearResults();
      });
    }

    disconnectedCallback() {
      if (this.input && this._onInput) this.input.removeEventListener('input', this._onInput);
      this._abortOngoingRequest();
    }

    _abortOngoingRequest() {
      if (this._abortController) {
        this._abortController.abort();
        this._abortController = null;
      }
    }

    _handleInput() {
      const term = (this.input.value || '').trim();
      if (!term) {
        this._clearResults();
        return;
      }

      if (this._cache.has(term)) {
        const cached = this._cache.get(term);
        this._renderResults(cached.html, cached.liveText);
        return;
      }

      this._getResults(term);
    }

    async _getResults(term) {
      this._abortOngoingRequest();
      this._setExpanded(true);
      this.resultsContainer.setAttribute('aria-busy', 'true');

      const predictiveSearchUrl =
        (window.routes && window.routes.predictive_search_url) ||
        (window.themeVariables && window.themeVariables.predictive_search_url) ||
        '/search/suggest';

      const url = new URL(predictiveSearchUrl, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('section_id', 'predictive-search');

      this._abortController = new AbortController();

      try {
        const response = await fetch(url.toString(), {
          signal: this._abortController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);

        const htmlText = await response.text();
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');

        const resultsRoot = doc.querySelector('.cusotm-predictive-search') || doc.body;
        const liveRegionValue = doc.querySelector('[data-predictive-search-live-region-count-value]');
        const liveText = liveRegionValue ? (liveRegionValue.textContent || '').trim() : '';
        const resultsHtml = resultsRoot ? resultsRoot.outerHTML : '';

        this._cache.set(term, { html: resultsHtml, liveText });
        this._renderResults(resultsHtml, liveText);
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        this.resultsContainer.setAttribute('aria-busy', 'false');
      } finally {
        this._abortController = null;
      }
    }

    _renderResults(resultsHtml, liveText) {
      if (!resultsHtml) {
        this._clearResults();
        return;
      }

      this.resultsContainer.innerHTML = resultsHtml;
      this.resultsContainer.setAttribute('aria-busy', 'false');
      this._setExpanded(true);

      if (this.status && liveText) {
        this.status.textContent = liveText;
        this.status.setAttribute('aria-hidden', 'false');
        window.setTimeout(() => {
          this.status.setAttribute('aria-hidden', 'true');
        }, 1000);
      }
    }

    _setExpanded(isExpanded) {
      this.input.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    }

    _clearResults() {
      this._abortOngoingRequest();
      this._setExpanded(false);
      this.resultsContainer.setAttribute('aria-busy', 'false');
      this.resultsContainer.innerHTML = this._initialResultsMarkup;
      if (this.status) {
        this.status.textContent = '';
        this.status.setAttribute('aria-hidden', 'true');
      }
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();
